import './NotificationPreferences.css';

import { Alert, Form, FormProps, Input, Modal, Row, Segmented, Select, Switch, Tabs, Tooltip } from 'antd';
import React, { useState, useEffect, useCallback } from 'react'
import { Badge, Breadcrumb, Card, Collapse, Container, Button } from 'react-bootstrap';
import { Notifications } from '../../api/Notifications';
import { useToasts } from '../../contexts/toasts';
import { TailSpin } from 'react-loader-spinner';
import { Applications } from '../../api/Applications';
import { ApplicationMode } from '../../components/list-items/BdaApplicationItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { formatDate, notificationTypesOptions } from '../../utils';
import { Link } from 'react-router-dom';
import IconButton from '../../components/common/IconButton';
import TriggerItem from '../../components/list-items/TriggerItem';
import { cloneDeep } from 'lodash';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { NotificationPreferences, NotificationTrigger } from '../../models/Notification';
import { BdaApplication } from '../../models/BdaApplication';
import { KrakendCollection } from '../../models/generics';
import TriggerEditForm from '../../components/forms/TriggerEditForm';

const layout: FormProps = {
  colon: false,
  labelCol: {
    xs: 24,
    sm: 12,
    md: 6
  },
  wrapperCol: {
    xs: 24,
    sm: 12,
    md: 18
  },
  labelAlign: 'left',
};

const tabs = [
  {
    key: 'web',
    label: (
      <>
        <FontAwesomeIcon icon={solid('globe')} />
        <span className="ms-2">Web Preferences</span>
      </>
    ),
  },
  {
    key: 'feed',
    label: (
      <>
        <FontAwesomeIcon icon={solid('rss')} />
        <span className="ms-2">Feed Preferences</span>
      </>
    ),
  },
  {
    key: 'triggers',
    label: (
      <>
        <FontAwesomeIcon icon={solid('bolt')} />
        <span className="ms-2">Triggers</span>
      </>
    ),
  },
]

export default function NotificationPreferencesPage() {
  const [form] = Form.useForm();
  const [applications, setApplications] = useState<BdaApplication[]>();
  const [userPreferences, setUserPreferences] = useState<NotificationPreferences>();
  const [showApplicationsPreferences, setShowApplicationsPreferences] = useState(true);
  const [showEmailPreferences, setShowEmailPreferences] = useState(true);
  const [error, setError] = useState(false);
  const [showSubmit, setShowSubmit] = useState(true);
  const [triggers, setTriggers] = useState<NotificationTrigger[]>();
  const [editing, setEditing] = useState<number>();
  const [editingTrigger, setEditingTrigger] = useState<NotificationTrigger>();
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [trigger, setTrigger] = useState<NotificationTrigger>();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  useEffect(() => {
    getPreferences();
    initApplications();
    // eslint-disable-next-line
  }, []);

  const initTriggers = useCallback(() => {
    Notifications.getTriggers()
      .then(
        ({ data }) => {
          const newTriggers = data.map(trigger => {
            const newTrigger = { ...trigger };
            if (trigger.headers) newTrigger.headers = Object.entries(trigger.headers as unknown as Record<string, string>).map(([key, value]) => ({ key, value }));
            if (trigger.queryParams) newTrigger.queryParams = Object.entries(trigger.queryParams as unknown as Record<string, string>).map(([key, value]) => ({ key, value }));
            return newTrigger;
          });
          setTriggers(newTriggers);
        }
      )
  }, []);

  useEffect(() => {
    if (userPreferences && !triggers) initTriggers();
  }, [userPreferences, triggers, initTriggers])

  useEffect(() => {
    form.setFieldsValue(userPreferences);
  }, [form, userPreferences]);

  const reset = () => {
    setUserPreferences(undefined);
    form.setFieldsValue(null);
    getPreferences();
  }

  const handleTriggerModalClose = () => {
    setEditingTrigger(new NotificationTrigger());
    setEditing(undefined);
    setShowTriggerModal(false);
  }
  const handleDeleteModalClose = () => setShowDeleteModal(false);
  const handleAddTrigger = () => setShowTriggerModal(true);

  const getPreferences = () => {
    Notifications.getNotificationPreferences()
      .then(
        ({ data }) => setUserPreferences(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('preferences-error', message, `Preferences Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  };

  const initApplications = () => {
    Applications.getApplications()
      .then(
        ({ data }) => setApplications((data as KrakendCollection<BdaApplication>).collection),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('applications-error', message, `Applications Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const togglePreferences = useCallback((group: 'applications' | 'email') => () => {
    if (group === "applications") setShowApplicationsPreferences(show => !show);
    else if (group === "email") setShowEmailPreferences(show => !show);
  }, []);

  const handleTabChange = useCallback((key: string) => {
    if (key === 'triggers') setShowSubmit(false);
    else setShowSubmit(true);
  }, []);

  const handleFormSubmit = (preferences: NotificationPreferences) => {
    showLoader();

    const body = { ...userPreferences, ...preferences };
    Notifications.saveNotificationPreferences(body)
      .then(
        () => {
          hideLoader();
          showToast('preferences-success', 'Preferences saved successfully', null, 'success');
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('preferences-error', message, `Preferences Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const handleTriggerSubmit = (trigger: NotificationTrigger) => {
    showLoader();
    const newHeaders = trigger.headers.reduce((acc, curr) => {
      acc[curr.key] = curr.value;
      return acc;
    }, {} as any);
    const newParams = trigger.queryParams.reduce((acc, curr) => {
      acc[curr.key] = curr.value;
      return acc;
    }, {} as any);

    const body = {
      ...trigger,
      headers: newHeaders,
      queryParams: newParams,
    }

    const promise = editing !== undefined
      ? Notifications.updateTrigger({ ...editingTrigger!, ...body })
      : Notifications.createTrigger(body);

    promise.then(
      () => {
        hideLoader();
        showToast(`trigger-${editing !== undefined ? 'update' : 'create'}-success`, `Trigger ${editing !== undefined ? 'updated' : 'created'} successfully`, 'Trigger Success', 'success');
        handleTriggerModalClose();
        setTriggers(undefined);
        initTriggers();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('triggers-error', message, `Triggers Error ${status ? `(${status})` : ''}`, 'danger');
      }
    );
  }

  const handleTriggerEdit = (index: number) => () => {
    setEditing(index);
    const trigger = cloneDeep(triggers![index]);
    setEditingTrigger(trigger);
    setShowTriggerModal(true);
  }

  const handleTriggerDelete = (trigger: NotificationTrigger) => () => {
    setTrigger(trigger);
    setShowDeleteModal(true);
  }

  const deleteTrigger = () => {
    Notifications.deleteTrigger(trigger!.id!)
      .then(
        () => {
          setShowDeleteModal(false);
          showToast(`trigger-delete-success`, "Trigger deleted successfully", 'Trigger Success', 'success');
          setTriggers(undefined);
          initTriggers();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('triggers-error', message, `Triggers Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const renderFeedPreferences = () => {
    return (
      <>
        <IconButton type="button" onClick={togglePreferences("email")} className="mb-3">
          <h6 className="mb-0">
            <FontAwesomeIcon icon={showEmailPreferences ? solid('chevron-down') : solid('chevron-right')} fixedWidth />
            <span className="ms-2">Email</span>
          </h6>
        </IconButton>

        <Collapse in={showEmailPreferences}>
          <div>
            <div className="ps-4">
              <Form.Item name={['feedPreferences', 'email', 'enabled']} label="Emails Enabled" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item noStyle dependencies={[['feedPreferences', 'email', 'enabled']]}>
                {({ getFieldValue }) => (
                  <Form.Item
                    name={['feedPreferences', 'email', 'address']}
                    label="Email address"
                    extra="Insert the email address you want to receive the notifications."
                    rules={[{ type: 'email', message: "Email must be a valid email" }]}
                  >
                    <Input placeholder="Email address" disabled={!getFieldValue(['feedPreferences', 'email', 'enabled'])} />
                  </Form.Item>
                )}
              </Form.Item>
            </div>
          </div>
        </Collapse>
      </>
    )
  };

  const renderWebPreferences = () => {
    return (
      <>
        <Form.Item className="mb-3" name={['webPreferences', 'enabled']} label="Web Notifications" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item noStyle dependencies={[['webPreferences', 'enabled']]}>
          {({ getFieldValue }) => !getFieldValue(['webPreferences', 'enabled']) && (
            <Alert
              showIcon
              type="warning"
              className="mb-3"
              message="If you disable Web Notifications, you will no longer receive new notifications and they will not show up in the history."
            />
          )}
        </Form.Item>

        <IconButton type="button" onClick={togglePreferences("applications")} className="mb-3">
          <h6 className="mb-0">
            <FontAwesomeIcon icon={showApplicationsPreferences ? solid('chevron-down') : solid('chevron-right')} fixedWidth />
            <span className="ms-2">Applications</span>
          </h6>
        </IconButton>

        <Collapse in={showApplicationsPreferences}>
          <div>
            <div className="ps-4">
              <Form.Item name={['webPreferences', 'applications', 'enabled']} label="Applications Notifications" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item
                name={['webPreferences', 'applications', 'monitoringMode']}
                label={
                  <>
                    <span className="me-2">Monitoring Mode</span>
                    <Tooltip title={
                      <>
                        Monitoring mode values:
                        <ul className="ps-3 m-0">
                          <li>
                            <b>Default</b> - Get notifications from applications you executed
                          </li>
                          <li>
                            <b>My Applications</b> - Get notifications about the applications you created
                          </li>
                          <li>
                            <b>Custom</b> - Get just the notifications from applications you specified
                          </li>
                        </ul>
                      </>
                    }
                    >
                      <FontAwesomeIcon icon={solid('info-circle')} />
                    </Tooltip>
                  </>
                }
              >
                <Segmented
                  options={[
                    { label: "Default", value: "DEFAULT" },
                    { label: "My Applications", value: "OWNED" },
                    { label: "Custom", value: "CUSTOM" },
                  ]}
                />
              </Form.Item>

              <Form.Item noStyle dependencies={[['webPreferences', 'applications', 'monitoringMode']]}>
                {({ getFieldValue }) => getFieldValue(['webPreferences', 'applications', 'monitoringMode']) === "CUSTOM" && (
                  <Form.Item
                    name={['webPreferences', 'applications', 'specificApplications']}
                    label="Specific Applications"
                    help="If you want to receive notifications about specific applications you can add them here."
                  >
                    <Select
                      allowClear
                      mode="multiple"
                      loading={!applications}
                      filterOption={(input, option) =>
                        ((option?.label ?? '') as string).toLowerCase().includes(input.toLowerCase())
                      }
                      placeholder="Select Applications"
                    >
                      {applications?.map(app => (
                        <Select.Option key={app.id} label={app.name} value={app.id}>
                          <ApplicationMode mode={app.workflowMode!} small />
                          <Badge bg="light" className="border text-secondary mx-2">
                            <FontAwesomeIcon icon={solid('tag')} />
                            <span className="ms-2">{app.version}</span>
                            {/* TODO AGGIUNGERE VERSIONE AL DTO */}
                          </Badge>
                          <span className="me-2">{app.name}</span>
                          {app.default && <FontAwesomeIcon className="text-warning" icon={solid('star')} />}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </Form.Item>
            </div>
          </div>
        </Collapse>
      </>
    )
  }

  const renderTriggers = () => {
    if (!triggers) return (
      <div className="py-5 d-flex justify-content-center">
        <TailSpin
          height={80}
          width={80}
          color="#677ab1"
          radius="1"
        />
      </div>
    )

    if (triggers.length === 0) return (
      <Alert
        showIcon
        type="info"
        message={
          <>
            <span className="me-auto">No triggers found.</span>
            <IconButton type="button" className="p-1 text-primary fw-500" onClick={handleAddTrigger}>Create one now!</IconButton>
          </>
        }
      />
    )

    return (
      <>
        <Row justify="end" className="mb-3">
          <Button type="button" variant="primary" size="sm" onClick={handleAddTrigger}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">Add Trigger</span>
          </Button>
        </Row>

        {triggers.map((trigger, index) => <TriggerItem key={trigger.id} trigger={trigger} onEdit={handleTriggerEdit(index)} onDelete={handleTriggerDelete(trigger)} />)}
      </>
    )
  }

  const renderTab = (key: string) => {
    switch (key) {
      case 'web':
      default:
        return renderWebPreferences();

      case 'feed':
        return renderFeedPreferences();

      case 'triggers':
        return renderTriggers();
    }
  }

  const renderPreferences = () => {
    return (
      <Card>
        <Form form={form} {...layout} onFinish={handleFormSubmit}>
          <Card.Body className="preferences-wrapper">
            <Form.Item name="notificationTypes" label="Notification Types" help="When empty, all notifications will be received.">
              <Select
                allowClear
                mode="multiple"
                placeholder="Select notification types"
                options={notificationTypesOptions}
              />
            </Form.Item>

            <Tabs
              defaultActiveKey="web"
              items={tabs.map(tab => ({
                ...tab,
                children: renderTab(tab.key),
              }))}
              onChange={handleTabChange}
            />
          </Card.Body>
          {showSubmit && (
            <Card.Footer className="d-flex">
              <Button type="submit" className="ms-auto px-3" variant="success" size="sm">
                <FontAwesomeIcon icon={solid('save')} />
                <span className="ms-2">Save</span>
              </Button>
            </Card.Footer>
          )}
        </Form>
      </Card>
    )
  }

  const renderAsyncContent = () => {
    if (error) return <Alert type="error" showIcon message="An error occured, try again later." />

    return userPreferences
      ? renderPreferences()
      : (
        <div className="py-5 d-flex justify-content-center">
          <TailSpin
            height={100}
            width={100}
            color="#677ab1"
            radius="1"
          />
        </div>
      )
  }

  return (
    <Container className="my-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/notifications' }}>Notifications</Breadcrumb.Item>
        <Breadcrumb.Item active>Preferences</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Notification Preferences</h5>
        {userPreferences && <small className="text-muted">Last updated: {formatDate(userPreferences.updated)}</small>}
      </div>

      {renderAsyncContent()}

      <Modal
        open={showTriggerModal}
        onCancel={handleTriggerModalClose}
        afterClose={handleTriggerModalClose}
        title={editing ? 'Edit Trigger' : 'Create Trigger'}
        footer={null}
        width={800}
      >
        <TriggerEditForm trigger={editingTrigger} onSubmit={handleTriggerSubmit} onCancel={handleTriggerModalClose} />
      </Modal>

      <Modal
        open={showDeleteModal}
        onCancel={handleDeleteModalClose}
        afterClose={handleDeleteModalClose}
        title="Delete Trigger"
        footer={
          <div className="d-flex justify-content-between mt-3">
            <Button size="sm" variant="light" className="border" onClick={handleDeleteModalClose}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={deleteTrigger}>
              <FontAwesomeIcon icon={solid('trash')} />
              <span className="ms-2">Delete</span>
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to delete trigger <b>{trigger?.name}</b>?</p>

        <Alert type="warning" showIcon message="Remember, this operation cannot be undone" />
      </Modal>
    </Container>
  )
}
